import { Card, Box, Typography } from "@mui/material";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    LabelList
} from "recharts";

export default function SkuSingleBarChart({
    title,
    yLabels,
    series,
    dataMatrix,
    xTicks, showUnit            // NEW PROP
}) {

    // Build chart data
    const chartData = yLabels.map((label, i) => {
        const row = { name: label };
        let total = 0;

        series.forEach((s, j) => {
            const value = dataMatrix[i]?.[j] ?? 0;
            row[s.label] = value;
            total += value;
        });

        row.total = total;
        return row;
    });

    // Total label at bar end
    const TotalLabel = ({ x, y, width, value }) => (
        <text
            x={x + width + 6}
            y={y + 12}
            fontSize={8}
            fill="#11111180"
            fontWeight={400}
        >
            {value}M
        </text>
    );
    const chartWidth = Math.max(yLabels.length * 90, 900);


    return (
        <Card
            sx={{
                p: 2,
                borderRadius: "12px !important",
                background: "#fff",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                position: "relative",
                overflow: "visible",
                height: "100%",
                width: "100%"
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    height: "36px",
                    background: "#115f9c",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)"
                }}
            >
                {title}
            </Box>

            <Box sx={{
                width: "100%",
                height: 420,
                overflowX: "auto",
                overflowY: "hidden"
            }}>
                <Box
                    sx={{
                        width: {
                            xs: chartWidth,   //  enable horizontal scroll on mobile
                            sm: chartWidth,   // enable horizontal scroll on tablet
                            md: chartWidth,
                            lg: "100%"
                        },
                        minWidth: {
                            xs: chartWidth,
                            sm: chartWidth,
                            md: chartWidth,
                            lg: "100%"
                        },
                        height: "100%",
                    }}
                >
                    <ResponsiveContainer>
                        <BarChart
                            data={chartData}
                            layout="vertical"
                            barSize={18}
                            margin={{ top: 40, right: 50, left: -30, bottom: 10 }}
                        >
                            <CartesianGrid stroke="#1111110D" horizontal={false} />

                            {/* X AXIS WITH CUSTOM TICKS */}
                            <XAxis
                                type="number"
                                ticks={xTicks}
                                tick={{ fontSize: 10, color: "#111111", fontWeight: 200 }}
                                axisLine={false}
                                tickLine={false}
                                domain={[0, Math.max(...xTicks)]}
                                tickFormatter={(v) => showUnit ? `${v}M` : v}
                            />


                            <YAxis
                                type="category"
                                dataKey="name"
                                width={260}
                                interval={0}
                                tick={({ payload, x, y, textAnchor }) => (
                                    <text
                                        x={x}
                                        y={y}
                                        textAnchor={textAnchor}
                                        fontSize={10}
                                        fill="#11111180"
                                        // dy={4}
                                        style={{
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            fontWeight: 200,
                                            cursor:"pointer"
                                        }}
                                    >
                                        <title>{payload.value}</title>
                                        {payload.value.length > 32
                                            ? `${payload.value.slice(0, 32)}...`
                                            : payload.value}
                                    </text>
                                )}
                            />

                            <Tooltip formatter={(v) => `${v}M`} />

                            {series.map((s, idx) => (
                                <Bar
                                    key={s.label}
                                    dataKey={s.label}
                                    stackId="a"
                                    fill={s.color}
                                    radius={[0, 0, 0, 0]}   // DEFAULT, NO ROUNDING
                                >
                                    {idx === series.length - 1 && (
                                        <LabelList
                                            dataKey="total"
                                            content={<TotalLabel />}
                                        />
                                    )}
                                </Bar>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                </Box>


            </Box>

            {/* Legend */}
            <Box sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: 1.5,
                mt: 1,
            }}>
                {series.map((s) => (
                    <Box key={s.label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                background: s.color
                            }}
                        />
                        <Typography fontSize={10} style={{ color: "#111111", fontWeight: 200 }}>{s.label}</Typography>
                    </Box>
                ))}
            </Box>
        </Card>
    );
}
